import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, X, Check, FileText, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Gate 3e — Renewal Welcome Modal.
 *
 * Surfaces a celebratory modal the first time a user logs in after the Square
 * webhook has flagged their account with a `pending_renewal_acknowledgment`
 * payload. Dismissing the modal clears the flag server-side so it never
 * re-appears (until the next renewal).
 *
 * Mounted globally inside Layout so it can fire from any page.
 */
export default function RenewalWelcomeModal() {
  const { user, refreshUser } = useAuth();
  const pending = user?.pending_renewal_acknowledgment;
  const [dismissing, setDismissing] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(!!pending);
  }, [pending]);

  const dismiss = async () => {
    setDismissing(true);
    try {
      await axios.post(`${API}/notifications/renewal-acknowledged`, {}, { withCredentials: true });
      setVisible(false);
      // Refresh user context so the flag clears app-wide
      if (refreshUser) refreshUser();
    } catch {/* silent */}
    finally { setDismissing(false); }
  };

  if (!visible || !pending) return null;

  const formatAmount = (a) => `$${Number(a || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const renewDate = pending.license_expires_at ? new Date(pending.license_expires_at).toLocaleDateString() : "";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="renewal-welcome-modal">
      <div className="bg-gradient-to-br from-white via-indigo-50 to-emerald-50 rounded-2xl max-w-lg w-full p-8 shadow-2xl border-2 border-indigo-200 relative">
        <button
          onClick={dismiss}
          disabled={dismissing}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">
            Welcome back to Tax Year {pending.tax_year}!
          </h2>
          <p className="text-slate-600 mt-2">
            Your <strong>{pending.tier_label || pending.tier}</strong> plan is renewed and ready.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
          <div className="bg-white/80 backdrop-blur border border-indigo-100 rounded-lg p-4">
            <div className="text-xs text-slate-500 uppercase font-medium">Page quota</div>
            <div className="text-xl font-bold text-indigo-700 mt-1">
              {Number(pending.pages_quota || 0).toLocaleString()}
            </div>
            <div className="text-xs text-slate-500 mt-1">OCR pages for this year</div>
          </div>
          <div className="bg-white/80 backdrop-blur border border-emerald-100 rounded-lg p-4">
            <div className="text-xs text-slate-500 uppercase font-medium">Next renewal</div>
            <div className="text-xl font-bold text-emerald-700 mt-1">{renewDate}</div>
            <div className="text-xs text-slate-500 mt-1">Locked in until then</div>
          </div>
        </div>

        {pending.amount_dollars > 0 && (
          <div className="mt-4 bg-white/60 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-600" />
            Payment of {formatAmount(pending.amount_dollars)} confirmed
          </div>
        )}

        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-emerald-600 hover:from-indigo-700 hover:to-emerald-700 text-white rounded-lg font-medium inline-flex items-center gap-2 shadow-md disabled:opacity-60"
            data-testid="renewal-welcome-dismiss-btn"
          >
            <FileText className="w-4 h-4" /> Get Started <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <p className="text-center text-xs text-slate-500 mt-4">
          You won't see this message again until your next renewal.
        </p>
      </div>
    </div>
  );
}
